import {
  MfaFactor,
  type WebAuthnRegistrationOptions,
  type WebAuthnAuthenticationOptions,
  type BindMfaPayload,
  type VerifyMfaPayload,
  InteractionEvent,
} from '@logto/schemas';

import api from '../api';

import { experienceApiRoutes } from './const';
import { initInteraction, submitInteraction } from './interaction';

const addMfa = async (type: MfaFactor, verificationId: string) =>
  api.post(`${experienceApiRoutes.mfa}`, {
    json: {
      type,
      verificationId,
    },
  });

type TotpSecretResponse = {
  verificationId: string;
  secret: string;
  secretQrCode: string;
};
type GetUserResponse = {
  email: string;
  phone: string;
  availableFactors: string[];
};

export const createTotpSecret = async (userId?: string) =>
  api
    .post(`${experienceApiRoutes.verification}/totp/secret${userId ? `?userId=${userId}` : ''}`)
    .json<TotpSecretResponse>();

export const createWebAuthnRegistration = async (userId?: string) => {
  const search = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  const { verificationId, registrationOptions } = await api
    .post(`${experienceApiRoutes.verification}/web-authn/registration${search}`)
    .json<{ verificationId: string; registrationOptions: WebAuthnRegistrationOptions }>();

  return {
    verificationId,
    options: registrationOptions,
  };
};

export const createWebAuthnAuthentication = async (userId?: string) => {
  const search = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  const { verificationId, authenticationOptions } = await api
    .post(`${experienceApiRoutes.verification}/web-authn/authentication${search}`)
    .json<{ verificationId: string; authenticationOptions: WebAuthnAuthenticationOptions }>();

  return {
    verificationId,
    options: authenticationOptions,
  };
};

export const createBackupCode = async () =>
  api.post(`${experienceApiRoutes.verification}/backup-code/generate`).json<{
    verificationId: string;
    codes: string[];
  }>();

export const skipMfa = async () => {
  await api.post(`${experienceApiRoutes.mfa}/mfa-skipped`);
  return submitInteraction();
};

export const bindMfa = async (payload: BindMfaPayload, verificationId: string) => {
  switch (payload.type) {
    case MfaFactor.TOTP: {
      const { code } = payload;
      await api.post(`${experienceApiRoutes.verification}/totp/verify`, {
        json: {
          code,
          verificationId,
        },
      });
      break;
    }
    case MfaFactor.WebAuthn: {
      await api.post(`${experienceApiRoutes.verification}/web-authn/registration/verify`, {
        json: {
          verificationId,
          payload,
        },
      });
      break;
    }
    case MfaFactor.BackupCode: {
      // No need to verify backup codes
      break;
    }
  }

  await addMfa(payload.type, verificationId);
  return submitInteraction();
};

/**
 * Verify the MFA payload only — does NOT call submitInteraction afterwards.
 * Used when we want to insert an extra step (e.g. "Add passkey" prompt) before
 * finalising the interaction.
 */
export const verifyMfaOnly = async (payload: VerifyMfaPayload, verificationId?: string) => {
  switch (payload.type) {
    case MfaFactor.TOTP: {
      const { code } = payload;
      await api.post(`${experienceApiRoutes.verification}/totp/verify`, {
        json: { code },
      });
      break;
    }
    case MfaFactor.WebAuthn: {
      await api.post(`${experienceApiRoutes.verification}/web-authn/authentication/verify`, {
        json: { verificationId, payload },
      });
      break;
    }
    case MfaFactor.BackupCode: {
      const { code } = payload;
      await api.post(`${experienceApiRoutes.verification}/backup-code/verify`, {
        json: { code },
      });
      break;
    }
  }
};

export const verifyMfa = async (payload: VerifyMfaPayload, verificationId?: string) => {
  await verifyMfaOnly(payload, verificationId);
  return submitInteraction();
};
export const getUser = async (user_id: string, skipInitInteraction = false) => {
  // Only initialize interaction if not already in OAuth callback context
  // OAuth callbacks with encrypted user_id need to preserve original session
  if (!skipInitInteraction) {
    await initInteraction(InteractionEvent.SignIn);
  }

  return api.get(`/api/experience/user/get/${user_id}`).json<GetUserResponse>();
};

export const updateRegisterEvent = async () => {
  await initInteraction(InteractionEvent.Register);
};
